import React, { useState } from "react";
import { Menu, Typography, Space, Dropdown, Button, Modal, Tooltip, message } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { ReactComponent as ExportIcon } from "../../assets/svg/export-icon.svg";
import { ReactComponent as ExportLoadingIcon } from "../../assets/svg/export-loading-icon.svg";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { selectUser, selectPatientTableColumns } from "../../store/selector";

import { GRAY_100, PRIMARY_600, GRAY_700, PRIMARY_50, BLACK, GRAY_400, GRAY_500, WHITE } from "../../constant/colors";
import { fontWeights } from "../../constant/styles";
import { ExportModalContent } from "../ExportUtils";
import { ReactComponent as ActionEditIcon } from "../../assets/svg/actions-edit-new.svg";
import { ReactComponent as ActionUrlIcon } from "../../assets/svg/actions-url.svg";
import { ReactComponent as ActionDeleteIcon } from "../../assets/svg/actions-delete-new.svg";
import { ReactComponent as WarningIcon } from "../../assets/svg/warning-icon-popup.svg";
import { ReactComponent as ArrowBackIcon } from "../../assets/svg/arrow-back-icon.svg";
import { ThreeDots } from "../SavedList/ThreeDots";
import { ActionsMenuStyle } from "./SavedListActionsMenuStyle";
import ACTIONS from "../../store/action";
import { Routes } from "../../constant/routes";
import { ViewType } from "../../constant/general";
import {
  FILE_TYPES,
  MAX_EXPORT_RECORDS,
  EXCLUDED_EXPORT_COLUMNS,
  EXPORT_MENU_IDS,
  DEFAULT_CONTENT_TYPE,
  DEFAULT_CONTENT_KEY
} from "../../constant/export";
import SuccessMessage from "../customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import { useExportWithTracking } from "../../hooks/useExportWithTracking";

const { Text } = Typography;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(-360deg);
  }
`;

const RotatingIconWrapper = styled.div`
  animation: ${rotate} 1s linear infinite;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledMenu = styled(Menu)`
  ${ActionsMenuStyle}
`;

const StyledButton = styled(Button)`
  border-radius: 15px;
  padding: 5px;
  :hover {
    background: ${GRAY_100};
    span {
      background-color: ${GRAY_700};
    }
  }
  :active,
  :focus {
    background: ${PRIMARY_50};
    span {
      background-color: ${PRIMARY_600};
    }
  }
`;

const SavedListActionsMenu = ({ source, record, recordCount, deleteAction }) => {
  const user = useSelector(selectUser);
  const patientTableColumns = useSelector(selectPatientTableColumns);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { exportWithTracking, isExporting } = useExportWithTracking();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [modal, contextHolder] = Modal.useModal();

  const setFormPopupsAct = (visible) => dispatch(ACTIONS.setFormPopups(visible));
  const populateSavedListPopup = (filterListData) =>
    dispatch(ACTIONS.setFilterList({ ...filterListData, isEditView: true }));

  const isOwner = user?.id === record?.user;

  // recordCount can be undefined (still loading), null (failed), or a number
  const isRecordCountLoading = recordCount === undefined;
  const isExportDisabled =
    isRecordCountLoading || recordCount === null || recordCount > MAX_EXPORT_RECORDS || recordCount === 0;

  const getVisibleColumns = () => {
    try {
      const savedListParams = typeof record.params === "string" ? JSON.parse(record.params) : record.params || {};

      const visibleColumns =
        savedListParams.selectedColumns && savedListParams.selectedColumns.length > 0
          ? savedListParams.selectedColumns
          : Object.keys(patientTableColumns || {}).filter((key) => patientTableColumns[key]?.mandatory === true);

      const filteredVisibleColumns = visibleColumns.filter((col) => !EXCLUDED_EXPORT_COLUMNS.includes(col));

      const staticColumns = Object.keys(patientTableColumns || {})
        .filter((key) => patientTableColumns[key]?.mandatory === true)
        .filter((col) => !EXCLUDED_EXPORT_COLUMNS.includes(col));

      const allColumns = [...new Set([...filteredVisibleColumns, ...staticColumns])];

      return allColumns;
    } catch (error) {
      return Object.keys(patientTableColumns || {}).filter((key) => patientTableColumns[key]?.mandatory === true);
    }
  };

  const handleExportConfirm = async (fileType) => {
    message.open({
      content: (
        <Space>
          <RotatingIconWrapper style={{ height: "36px" }}>
            <ExportLoadingIcon style={{ width: 36, height: 36 }} />
          </RotatingIconWrapper>
          <Text style={{ color: BLACK }}>{t("export.preparing_export")}</Text>
        </Space>
      ),
      key: "exportMessage",
      duration: 0,
      className: "export-loading-message"
    });
    const visibleColumns = getVisibleColumns();

    // Get saved list params and extract sorting and filters
    const savedListParams = typeof record.params === "string" ? JSON.parse(record.params) : record.params || {};
    const savedSorting = savedListParams.sorting || {};
    const savedFilters = savedListParams.filters || {};

    await exportWithTracking(
      {
        listType: "savedList",
        selectedView: "pharmacy-list",
        filters: savedFilters,
        sorting: savedSorting,
        visibleColumns,
        savedListId: record.id,
        fileType,
        listName: record.name
      },
      {
        pageKey: "pharmacy-list",
        contentType: DEFAULT_CONTENT_TYPE,
        contentKey: DEFAULT_CONTENT_KEY,
        exportType: fileType,
        numberOfRecords: recordCount,
        sorting: savedSorting,
        filters: savedFilters,
        visibleColumns,
        isSavedList: true,
        savedListId: record.id,
        savedListName: record.name,
        savedListOriginBucket: "pharmacy-list"
      }
    );

    message.destroy("exportMessage");
  };

  const handleExport = (fileType) => {
    setDropdownVisible(false);
    setShowExportMenu(false);

    if (isExportDisabled) {
      if (recordCount === 0) {
        ErrorMessage(t("export.no_records_to_export"));
      } else if (recordCount > MAX_EXPORT_RECORDS) {
        ErrorMessage(t("export.record_limit_exceeded_tooltip", { limit: MAX_EXPORT_RECORDS.toLocaleString() }));
      }
      return;
    }

    const modalInstance = modal.confirm({
      centered: true,
      className: "formPopupsModal",
      okText: t("export.modal_confirm_button"),
      cancelText: t("cancel"),
      icon: <WarningIcon />,
      title: t("export.modal_title"),
      content: <ExportModalContent count={recordCount} fileType={fileType} />,
      okButtonProps: {
        loading: isExporting
      },
      onOk: () => {
        modalInstance.destroy();
        handleExportConfirm(fileType);
      }
    });
  };

  const handleVisibleChange = (visible) => {
    setDropdownVisible(visible);
    if (!visible) {
      setShowExportMenu(false);
    }
  };

  const actionsMenuItems = [
    {
      id: `${source}_options_edit`,
      key: "edit",
      title: t("savedList.actions_menu.edit"),
      icon: <ActionEditIcon />,
      show: isOwner && record.isValid,
      actions: () => {
        populateSavedListPopup(record);
        setFormPopupsAct(true);
      }
    },
    {
      key: "copy",
      title: t("savedList.actions_menu.copy"),
      icon: <ActionUrlIcon />,
      id: "saved_lists_copy_list_link",
      show: record.isValid,
      actions: () => {
        const { id } = record;
        const url = `${window.location.origin}${Routes.ROOT}${Routes.SAVEDLISTS}/${id}/${ViewType.List.key}`;
        navigator.clipboard.writeText(url);
        SuccessMessage(t("savedList.copy_action"));
      }
    },
    {
      key: "export",
      title: t("export.export_list"),
      icon: <ExportIcon />,
      id: EXPORT_MENU_IDS.LIST,
      show: record.isValid,
      disabled: isExportDisabled,
      hasSubmenu: true,
      disabledTooltip:
        !isRecordCountLoading && recordCount > MAX_EXPORT_RECORDS
          ? t("export.record_limit_exceeded_tooltip", { limit: MAX_EXPORT_RECORDS.toLocaleString() })
          : null,
      actions: () => {
        if (!isExportDisabled) {
          setShowExportMenu(true);
        }
      }
    },
    {
      id: `${source}_options_delete`,
      key: "delete",
      title: t("savedList.actions_menu.delete"),
      icon: <ActionDeleteIcon />,
      show: isOwner,
      actions: () => {
        deleteAction(record);
      }
    }
  ];

  const itemsToShow = actionsMenuItems.filter((item) => item.show);

  const exportMenuItems = (
    <StyledMenu style={{ width: "89px" }}>
      <Menu.Item key="back" onClick={() => setShowExportMenu(false)}>
        <Space>
          <LeftOutlined style={{ fontSize: "12px" }} />
        </Space>
      </Menu.Item>
      <Menu.Item key={FILE_TYPES.CSV} onClick={() => handleExport(FILE_TYPES.CSV)} id={EXPORT_MENU_IDS.CSV}>
        <Text>{t("export.csv_option")}</Text>
      </Menu.Item>
      <Menu.Item key={FILE_TYPES.XLSX} onClick={() => handleExport(FILE_TYPES.XLSX)} id={EXPORT_MENU_IDS.XLSX}>
        <Text>{t("export.xlsx_option")}</Text>
      </Menu.Item>
    </StyledMenu>
  );

  const mainMenuItems = (
    <StyledMenu>
      {itemsToShow.map(({ id, key, actions, title, icon, disabled = false, disabledTooltip, hasSubmenu }) => {
        const menuItemContent = (
          <Menu.Item
            id={id}
            key={key}
            onClick={() => !disabled && actions(record)}
            disabled={disabled}
            style={{
              ...(key === "delete" ? { borderTop: "1px solid " + GRAY_100 } : {}),
              ...(disabled ? { cursor: "not-allowed" } : {})
            }}
          >
            <Space style={{ justifyContent: "space-between", width: "100%" }}>
              <Text style={disabled ? { color: GRAY_400 } : {}}>
                {icon && (
                  <span
                    style={{
                      marginRight: 9,
                      verticalAlign: key === "export" ? "baseline" : "sub"
                    }}
                  >
                    {React.cloneElement(icon, { style: disabled ? { color: GRAY_400 } : {} })}
                  </span>
                )}
                {title}
              </Text>
              {hasSubmenu && (
                <ArrowBackIcon
                  style={{
                    width: "6px",
                    height: "10px",
                    transform: "rotate(180deg)",
                    color: disabled ? GRAY_400 : GRAY_500
                  }}
                />
              )}
            </Space>
          </Menu.Item>
        );

        return disabled && disabledTooltip ? (
          <Tooltip
            key={key}
            title={disabledTooltip}
            placement="left"
            overlayInnerStyle={{
              padding: "10px 10px",
              width: "280px",
              color: BLACK,
              backgroundColor: WHITE,
              fontWeight: fontWeights.regular,
              fontSize: "12px",
              borderRadius: "8px"
            }}
            overlayStyle={{ maxWidth: "300px" }}
            color={WHITE}
          >
            <div>{menuItemContent}</div>
          </Tooltip>
        ) : (
          menuItemContent
        );
      })}
    </StyledMenu>
  );

  const overlayItems = showExportMenu ? exportMenuItems : mainMenuItems;

  const dropDown = Boolean(isOwner || record.isValid) && (
    <Dropdown
      dropdownRender={() => overlayItems}
      placement="bottomRight"
      trigger={["click"]}
      open={dropdownVisible}
      onOpenChange={handleVisibleChange}
    >
      <StyledButton type="text" id="saved_lists_options_link">
        <ThreeDots />
      </StyledButton>
    </Dropdown>
  );

  return (
    <>
      {dropDown}
      {contextHolder}
    </>
  );
};

export { SavedListActionsMenu };
